import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import SessionProgressIndicator from '../../components/ui/SessionProgressIndicator';
import ImageUploader from './components/ImageUploader';
import ManualCodeEntry from './components/ManualCodeEntry';
import ScanInstructions from './components/ScanInstructions';
import ScanResultHandler from './components/ScanResultHandler';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { analyzeReceiptMock, analyzeReceipt } from '../../utils/receiptAnalysis';
import { saveSessionToCloud, createInitialUserRecord } from '../../utils/supabaseService';
import { setCurrentUser, clearAllSessionData } from '../../utils/sessionStorage';

const ReceiptScanner = () => {
  const navigate = useNavigate();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState('');
  const [useApiMode, setUseApiMode] = useState(false); // Toggle between mock and real API
  const [showNameModal, setShowNameModal] = useState(false);
  const [userName, setUserName] = useState('');
  const [pendingImage, setPendingImage] = useState(null);
  const [manualEntryMode, setManualEntryMode] = useState(false);

  useEffect(() => {
    // Check if API key is configured
    const hasApiKey = !!import.meta.env.VITE_OPENAI_API_KEY;
    setUseApiMode(hasApiKey);
  }, []);

  const handleImageCapture = async (imageBase64) => {
    // Primero pedir el nombre del usuario
    setPendingImage(imageBase64);
    setShowNameModal(true);
  };

  const handleNameSubmit = async () => {
    if (!userName.trim()) {
      alert('Por favor ingresa tu nombre');
      return;
    }

    setShowNameModal(false);
    // If the user chose manual-entry mode, create an empty bill and navigate to bill-overview
    if (manualEntryMode) {
      // 🧹 Limpiar datos de sesiones anteriores (en modo manual no pasó por ImageUploader)
      console.log('🧹 Limpiando datos de sesiones anteriores (modo manual)...');
      clearAllSessionData();

      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const seconds = now.getSeconds().toString().padStart(2, '0');
      const oderId = `${userName.trim()}_${hours}${minutes}${seconds}`;
      const manualId = `manual-${Date.now()}`;

      // 💾 Guardar usuario activo en localStorage
      setCurrentUser(manualId, oderId, userName.trim(), true);

      const manualBill = {
        id: manualId,
        ticketNumber: manualId,
        restaurant: { name: 'Manual' },
        tableNumber: null,
        date: new Date().toISOString(),
        subtotal: 0,
        tax: 0,
        serviceCharge: 0,
        total: 0,
        participants: [
          { id: oderId, name: userName.trim(), status: 'connected', isHost: true, joinedAt: new Date().toISOString() }
        ],
        items: []
      };

      setManualEntryMode(false);
      navigate('/bill-overview', {
        state: {
          billData: manualBill,
          userName: userName.trim(),
          userId: oderId,
          isHost: true
        }
      });
      return;
    }

    setIsAnalyzing(true);
    setScanError('');


    try {
      // Use mock or real API based on configuration
      const result = useApiMode
        ? await analyzeReceipt(pendingImage)
        : await analyzeReceiptMock(pendingImage);

      if (result.success) {
        // Crear estructura de sesión completa
        const sessionData = {
          id: result.sessionId,
          ticketNumber: result.sessionId,
          restaurant: {
            name: result.data.restaurant || 'Restaurante',
            address: '',
            phone: ''
          },
          tableNumber: null,
          date: result.data.date || new Date().toISOString(),
          subtotal: result.data.subtotal || 0,
          tax: result.data.tax || 0,
          serviceCharge: 0,
          total: result.data.total || 0,
          participants: [
            {
              id: `${userName.trim()}_${new Date().getHours().toString().padStart(2, '0')}${new Date().getMinutes().toString().padStart(2, '0')}${new Date().getSeconds().toString().padStart(2, '0')}`,
              name: userName.trim(),
              status: 'connected',
              isHost: true,
              joinedAt: new Date().toISOString()
            }
          ],
          items: (result.data.items || []).map((item, index) => ({
            id: `item-${index + 1}`,
            name: item.name,
            description: '',
            category: 'general',
            quantity: item.quantity || 1,
            unitPrice: item.unitPrice || item.totalPrice,
            totalPrice: item.totalPrice,
            tags: [],
            validated: false
          }))
        };

        // Generar userId único: nombreUsuario_HHmmss
        const now = new Date();
        const hours = now.getHours().toString().padStart(2, '0');
        const minutes = now.getMinutes().toString().padStart(2, '0');
        const seconds = now.getSeconds().toString().padStart(2, '0');
        const oderId = `${userName.trim()}_${hours}${minutes}${seconds}`;
        console.log('👤 Generated userId:', oderId);

        // 💾 Guardar usuario activo en localStorage
        setCurrentUser(result.sessionId, oderId, userName.trim(), true);

        // Guardar sesión en Supabase inmediatamente
        console.log('💾 Guardando nueva sesión en Supabase:', result.sessionId);
        const saveResult = await saveSessionToCloud(result.sessionId, sessionData);

        if (!saveResult.success) {
          console.error('❌ Error guardando sesión:', saveResult.error);
          throw new Error('Error al guardar la sesión en la base de datos');
        }

        console.log('✅ Sesión guardada exitosamente en Supabase');

        // Crear registro inicial del usuario en user_claims
        console.log('👤 Creando registro inicial de usuario en user_claims');
        const userRecordResult = await createInitialUserRecord(result.sessionId, oderId, userName.trim());

        if (!userRecordResult.success) {
          console.error('❌ Error creando registro de usuario:', userRecordResult.error);
          // No bloqueamos el flujo si falla esto
        } else {
          console.log('✅ Registro de usuario creado exitosamente');
        }

        setScanResult({
          sessionId: result.sessionId,
          data: result.data,
          userName: userName.trim(),
          userId: oderId
        });
      } else {
        throw new Error(result.error || 'Error al analizar el ticket');
      }
    } catch (error) {
      setScanError(error?.message || 'Error al procesar la imagen del ticket');
    } finally {
      setIsAnalyzing(false);
      setPendingImage(null);
    }
  };

  const handleScanError = (error) => {
    setScanError(error);
  };

  const handleManualCodeSubmit = (code) => {
    // For backward compatibility with manual QR codes
    setScanResult({
      sessionId: code,
      data: null
    });
  };

  const handleRetry = () => {
    setScanResult(null);
    setScanError('');
  };

  const handleReset = () => {
    setScanResult(null);
    setScanError('');
  };

  // Show scan result handler if we have a result, error, or analyzing
  if (scanResult || scanError || isAnalyzing) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <SessionProgressIndicator currentStep={0} />

        <main className="container mx-auto px-4 py-8">
          <ScanResultHandler
            scanResult={scanResult?.sessionId}
            receiptData={scanResult?.data}
            userName={scanResult?.userName}
            userId={scanResult?.userId}
            isLoading={isAnalyzing}
            error={scanError}
            onRetry={handleRetry}
            onReset={handleReset}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <SessionProgressIndicator currentStep={0} />

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Left Column - Instructions */}
          <div className="order-2 lg:order-1">
            <ScanInstructions
              isScanning={isAnalyzing}
            />

            {/* API Mode indicator */}
            <div className="mt-4 p-3 bg-muted rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name={useApiMode ? "Zap" : "TestTube"} size={16} className="text-text-secondary mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-text-secondary">
                    {useApiMode
                      ? "🤖 Modo API: Se usará OpenAI para analizar el ticket"
                      : "🧪 Modo Demo: Se usarán datos de ejemplo (no se requiere API key)"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Scanner/Permission Handler */}
          <div className="order-1 lg:order-2 space-y-6">
            {/* Image Uploader - Always visible as primary option */}
            <ImageUploader
              onImageSelected={handleImageCapture}
              isAnalyzing={isAnalyzing}
              onManualEntry={() => { setManualEntryMode(true); setShowNameModal(true); }}
              onSessionReset={clearAllSessionData}
            />
          </div>
        </div>

        {/* Bottom Help Section */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="font-semibold text-foreground mb-2">
              ¿Necesitas ayuda?
            </h3>
            <p className="text-sm text-text-secondary mb-4">
              Si tienes problemas escaneando el ticket,
              no dudes en preguntar al personal del restaurante.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-xs text-text-secondary">
              <span>• Asegúrate de que el ticket esté bien iluminado</span>
              <span>• El texto debe ser legible</span>
              <span>• Evita sombras y reflejos</span>
            </div>
          </div>
        </div>
      </main>

      {/* Name Modal */}
      {showNameModal && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              setShowNameModal(false);
              setPendingImage(null);
            }}
          />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto">
            <div className="bg-surface border border-border rounded-lg shadow-elevation-3">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4 mx-auto">
                  <Icon name="User" size={24} className="text-primary" />
                </div>

                <h3 className="text-lg font-semibold text-foreground text-center mb-2">
                  ¿Cuál es tu nombre?
                </h3>

                <p className="text-sm text-text-secondary text-center mb-4">
                  Así podrás identificar tus artículos en la cuenta compartida
                </p>

                <Input
                  type="text"
                  placeholder="Ej: Juan Pérez"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleNameSubmit();
                    }
                  }}
                  autoFocus
                  className="mb-4"
                />

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowNameModal(false);
                      setPendingImage(null);
                      setUserName('');
                    }}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleNameSubmit}
                    disabled={!userName.trim()}
                    className="flex-1"
                  >
                    Continuar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReceiptScanner;